import { computed, toValue, type MaybeRefOrGetter } from "vue";
import { usePage } from "@inertiajs/vue3";
import type { IRhizomPageProps } from "@/types/RhizomPageProps/RhizomPageProps";

const RUA_DOMAIN = "rua.dmarceye.com";

const DEFAULTS = {
    p: "none",
    pct: "100",
    fo: "1",
    ri: "86400",
} as const;

const VALID_POLICIES = new Set(["none", "quarantine", "reject"]);
const VALID_FO_TOKENS = new Set(["0", "1", "d", "s"]);

function buildOurRua(teamUuid: string): string {
    return `mailto:${teamUuid}@${RUA_DOMAIN}`;
}

function isDmarcRecord(raw: string): boolean {
    return /(?:^|;\s*)v\s*=\s*dmarc1\b/i.test(raw);
}

function extractTag(raw: string, tag: string): string | null {
    const re = new RegExp(`(?:^|;\\s*)${tag}\\s*=\\s*([^;]+)`, "i");
    const m = raw.match(re);
    return m ? (m[1]?.trim() ?? null) : null;
}

function ruaContainsOurAddress(ruaValue: string, ourRua: string): boolean {
    return ruaValue
        .split(",")
        .map((s) => s.trim().toLowerCase())
        .includes(ourRua.toLowerCase());
}

function buildDefaultRecord(ourRua: string): string {
    return `v=DMARC1; p=${DEFAULTS.p}; rua=${ourRua}; fo=${DEFAULTS.fo};`;
}

function buildRecordFromMalformed(raw: string, ourRua: string): string {
    const pRaw = (extractTag(raw, "p") ?? "").toLowerCase();
    const p = VALID_POLICIES.has(pRaw) ? pRaw : DEFAULTS.p;

    const foRaw = extractTag(raw, "fo") ?? "";
    const foTokens = foRaw
        .split(":")
        .map((s) => s.trim())
        .filter((s) => VALID_FO_TOKENS.has(s));
    const fo = foTokens.length ? foTokens.join(":") : DEFAULTS.fo;

    const riRaw = extractTag(raw, "ri") ?? "";
    const riParsed = parseInt(riRaw, 10);
    const ri =
        Number.isFinite(riParsed) && riParsed > 0
            ? String(riParsed)
            : DEFAULTS.ri;

    const pctRaw = extractTag(raw, "pct") ?? "";
    const pctParsed = parseInt(pctRaw, 10);
    const pct = Number.isFinite(pctParsed)
        ? String(Math.min(100, Math.max(0, pctParsed)))
        : DEFAULTS.pct;

    const existingRua = extractTag(raw, "rua") ?? "";
    const rua =
        existingRua.trim() !== ""
            ? ruaContainsOurAddress(existingRua, ourRua)
                ? existingRua
                : `${existingRua},${ourRua}`
            : ourRua;

    const tags = [`v=DMARC1`, `p=${p}`, `rua=${rua}`];
    if (fo) tags.push(`fo=${fo}`);
    if (ri !== DEFAULTS.ri) tags.push(`ri=${ri}`);
    if (pct !== DEFAULTS.pct) tags.push(`pct=${pct}`);
    return tags.join("; ") + ";";
}

function injectOurRua(raw: string, ourRua: string): string {
    const existingRua = extractTag(raw, "rua");

    if (existingRua === null) {
        const trimmed = raw.trim();
        const base = trimmed.endsWith(";") ? trimmed : `${trimmed};`;
        return `${base} rua=${ourRua};`;
    }

    const addresses = existingRua
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    const merged = [...addresses, ourRua].join(",");

    return raw.replace(/((?:^|;\s*)rua\s*=\s*)([^;]+)/i, `$1${merged}`);
}

export function useDmarcRecordPreparer(
    rawRecord: MaybeRefOrGetter<string | null | undefined>,
) {
    const page = usePage<IRhizomPageProps>();

    const preparedRecord = computed((): string => {
        const teamUuid = page.props.team?.uuid ?? "";
        const raw = (toValue(rawRecord) ?? "").trim();
        const ourRua = buildOurRua(teamUuid);

        if (!raw) {
            return buildDefaultRecord(ourRua);
        }

        if (!isDmarcRecord(raw)) {
            return buildRecordFromMalformed(raw, ourRua);
        }

        const ruaValue = extractTag(raw, "rua");

        if (ruaValue !== null && ruaContainsOurAddress(ruaValue, ourRua)) {
            return raw.endsWith(";") ? raw : `${raw};`;
        }

        return injectOurRua(raw, ourRua);
    });

    return { preparedRecord };
}
